import { Injectable, inject } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import { OfferStatus } from '@core/offers-types';
import type { Placement } from '@core/placements-types';
import type { Stage } from '@core/stages-types';
import { StageCategory, StageGroup } from '@core/stages-types';

import type { StageWithProgress } from './types';

@Injectable({
  providedIn: 'root',
})
export class StagesWithProgressService {
  private readonly i18n = inject(I18nService);

  private canManageHiredCandidates: boolean;
  private canProceed: boolean;
  private currentStage: Stage;
  private allStages: Stage[];
  private placement: Placement;

  public get(
    stages: Stage[],
    placement: Placement,
    canProceed: boolean = false,
    canManageHiredCandidates: boolean = false,
  ): StageWithProgress[] {
    this.canManageHiredCandidates = canManageHiredCandidates;
    this.canProceed = canProceed;
    this.currentStage = placement.stage;
    this.allStages = stages;
    this.placement = placement;

    const useStageGrouping = this.isMultipleHiredStages();

    return stages.map(stage => {
      const progress = this.getStageProgress(stage, placement);
      const { accessToGroup, tooltipCopy } = this.checkAccessToGroups(stage);

      return {
        ...stage,
        progress,
        hasAutomatedActions: !!stage.actionTemplates && stage.actionTemplates.length > 0,
        timeLimit: stage.timeLimit || null,
        useStageGrouping,
        isFirstGroupStage: this.checkWhetherIsFirstGroupStage(stages, stage),
        accessToGroup,
        tooltipCopy,
      };
    });
  }

  private getStageProgress(stage: Stage, placement: Placement): 'current' | 'next' | 'prev' {
    if (placement && placement.stage) {
      if (placement.stage.id === stage.id) {
        return 'current';
      } else if (stage.position > placement.stage.position) {
        return 'next';
      } else {
        return 'prev';
      }
    } else {
      return 'next';
    }
  }

  private isMultipleHiredStages(): boolean {
    return this.allStages.filter(stage => stage.group === StageGroup.Hires).length > 1;
  }

  private checkWhetherIsFirstGroupStage(allStages: Stage[], stage: Stage): boolean {
    const stageGroup = stage.group;

    if (!stageGroup) {
      return false;
    }

    const allStagesGroup = allStages.filter(
      stage =>
        (!stage.locked ||
          (stage.locked &&
            (stage.category === StageCategory.Apply ||
              stage.category === StageCategory.Sourced))) &&
        stage.group === stageGroup,
    );

    return allStagesGroup.indexOf(stage) === 0;
  }

  private isHiresStage(stage: Stage): boolean {
    return stage.group === StageGroup.Hires;
  }

  private checkAccessToGroups(stage: Stage): {
    accessToGroup: boolean;
    tooltipCopy?: string;
  } {
    const defaultValue = {
      accessToGroup: true,
    };
    const isJobArchived = this.placement.offer?.status === OfferStatus.Archived;

    if (isJobArchived && stage.id !== this.currentStage.id) {
      return {
        accessToGroup: false,
        tooltipCopy: this.i18n.translate('app.stages.no_proceed_for_archived_job'),
      };
    } else if (this.isHiresStage(stage) && !this.canManageHiredCandidates) {
      return {
        accessToGroup: false,
        tooltipCopy: this.i18n.translate('app.stages.no_hires_permission'),
      };
    } else if (!this.canProceed && stage.id !== this.currentStage.id) {
      return {
        accessToGroup: false,
        tooltipCopy: this.i18n.translate('app.stages.no_proceed_permission'),
      };
    }

    return defaultValue;
  }
}
